import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, forkJoin, of } from "rxjs";

export interface AlertSettings {
  // Configurações de TTS
  speechEnabled: boolean;
  speechVolume: number; // 0-1
  speechRate: number; // 0.5-2
  speechPitch: number; // 0-2
  speechVoice: string;

  // Configurações visuais
  alertDuration: number; // em segundos
  alertPosition: "top" | "center" | "bottom";
  showAnimation: boolean;
  backgroundColor: string;
  textColor: string;

  // Configurações de filtros
  minAmount: number;
  queueMode: boolean;

  // Templates de mensagem
  template: string;
  
  // 🎵 Áudio customizado
  voiceRecordingUrl?: string | null;

  alertType?: "DONATION" | "SALE" | "GOAL"; // para diferenciar tipos
}

export interface GoalStep {
  goalDescricao: string;
  goalValueInicial: number;
  goalValueFinal: number;
  goalMostrarPreview: boolean;
  isCompleted: boolean;
  completedAt?: Date;
  actualValueWhenCompleted?: number;
}

export interface GoalSettings {
  alertType: "GOAL";
  goalIncluirDoacoes: boolean;
  goalIncluirVendas: boolean;
  // Configurações visuais da tabela goalvisualsetting (nomes em inglês)
  barColor: string;
  textColor: string;
  valueColor: string; // Cor para texto dentro da barra
  backgroundColor: string;
  showExplosion: boolean;
  showGoalText: boolean;
  goalCompletedText: string;
  
  // NOVO: Sistema multi-etapas
  goalSteps: GoalStep[];
  currentStepIndex: number;
  totalRaised: number; // Valor real do backend
  configuredValue: number; // Valor configurável pelo usuário
}

@Injectable({
  providedIn: "root",
})
export class AlertService {
  // Configuração padrão
  private readonly DEFAULT_SETTINGS: AlertSettings = {
    speechEnabled: true,
    speechVolume: 100,
    speechRate: 1,
    speechPitch: 1,
    speechVoice: "default", // será substituído pelo defaultVoice real

    alertDuration: 10,
    alertPosition: "center",
    showAnimation: true,

    minAmount: 0,
    queueMode: true,

    template: "",

    backgroundColor: "#ffffff",
    textColor: "#2a2d2d",
  };

  // Configuração padrão para metas
  private readonly DEFAULT_GOAL_SETTINGS: GoalSettings = {
    alertType: "GOAL",
    goalIncluirDoacoes: true,
    goalIncluirVendas: true,
    // Configurações visuais da tabela goalvisualsetting (nomes em inglês)
    barColor: "#9c27b0",
    textColor: "#333333",
    valueColor: "#ffffff", // Cor para texto dentro da barra
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    showExplosion: true,
    showGoalText: false,
    goalCompletedText: "Goal achieved!",
    
    // NOVO: Sistema multi-etapas
    goalSteps: [
      {
        goalDescricao: "Nova meta",
        goalValueInicial: 0,
        goalValueFinal: 1000,
        goalMostrarPreview: false,
        isCompleted: false,
        completedAt: undefined,
        actualValueWhenCompleted: undefined
      }
    ],
    currentStepIndex: 0,
    totalRaised: 0,
    configuredValue: 0
  };

  private donationSettings$ = new BehaviorSubject<AlertSettings>(
    this.DEFAULT_SETTINGS
  );
  private saleSettings$ = new BehaviorSubject<AlertSettings>(
    this.DEFAULT_SETTINGS
  );
  private goalSettings$ = new BehaviorSubject<GoalSettings>(
    this.DEFAULT_GOAL_SETTINGS
  );

  constructor() {
    this.initializeDefaultVoices();
  }

  public loadConfigFromSocket(alertsSettings: AlertSettings[]): void {
    alertsSettings.forEach((settings) => {
      switch (settings.alertType) {
        case "DONATION":
          this.donationSettings$.next({
            ...this.DEFAULT_SETTINGS,
            ...settings,
            queueMode: true, // 🔒 Forçar fila sempre ativa
          });
          break;
        case "SALE":
          this.saleSettings$.next({ 
            ...this.DEFAULT_SETTINGS, 
            ...settings,
            queueMode: true, // 🔒 Forçar fila sempre ativa
          });
          break;
        case "GOAL":
          this.goalSettings$.next({ ...this.DEFAULT_GOAL_SETTINGS, ...(settings as any) });
          break;
      }
    });
  }

  // Inicializa a voz padrão
  // Guarda a voz que será usada para cada tipo
  private defaultVoices: Record<"donation" | "sale", SpeechSynthesisVoice> = {
    donation: this.getFallbackVoice(),
    sale: this.getFallbackVoice(),
  };

  private initializeDefaultVoices(): void {
    if (!("speechSynthesis" in window)) return;

    const selectVoices = () => {
      const voices = window.speechSynthesis.getVoices();

      // DONATION
      const donationVoiceUri = this.donationSettings$.value.speechVoice;
      this.defaultVoices.donation =
        voices.find((v) => v.voiceURI === donationVoiceUri) ||
        this.getFallbackVoice();

      // SALE
      const saleVoiceUri = this.saleSettings$.value.speechVoice;
      this.defaultVoices.sale =
        voices.find((v) => v.voiceURI === saleVoiceUri) ||
        this.getFallbackVoice();
    };

    if (window.speechSynthesis.getVoices().length > 0) {
      selectVoices();
    } else {
      window.speechSynthesis.addEventListener("voiceschanged", selectVoices, {
        once: true,
      });
    }
  }

  // Getters para configurações atuais
  public getSettings(type: "donation"): AlertSettings;
  public getSettings(type: "sale"): AlertSettings;
  public getSettings(type: "goal"): GoalSettings;
  public getSettings(type: "donation" | "sale" | "goal"): AlertSettings | GoalSettings {
    switch (type) {
      case "donation":
        return this.donationSettings$.value;
      case "sale":
        return this.saleSettings$.value;
      case "goal":
        return this.goalSettings$.value;
      default:
        return this.donationSettings$.value;
    }
  }

  // Observables para componentes se inscreverem
  get settingsDonation() {
    return this.donationSettings$.asObservable();
  }

  get settingsSale() {
    return this.saleSettings$.asObservable();
  }

  get settingsGoal() {
    return this.goalSettings$.asObservable();
  }

  // Métodos para atualizar configurações
  public updateSettings(type: "donation" | "sale" | "goal", newSettings: Partial<AlertSettings> | Partial<GoalSettings>): void {
    switch (type) {
      case "donation":
        this.donationSettings$.next({
          ...this.donationSettings$.value,
          ...(newSettings as Partial<AlertSettings>),
        });
        break;
      case "sale":
        this.saleSettings$.next({
          ...this.saleSettings$.value,
          ...(newSettings as Partial<AlertSettings>),
        });
        break;
      case "goal":
        this.goalSettings$.next({
          ...this.goalSettings$.value,
          ...(newSettings as Partial<GoalSettings>),
        });
        break;
    }
  }

  // Helper para acessar vozes
  public getDefaultVoice(type: "donation" | "sale"): SpeechSynthesisVoice {
    return this.defaultVoices[type];
  }

  // Retorna qualquer voz em português como fallback
  private getFallbackVoice(): SpeechSynthesisVoice {
    const voices =
      "speechSynthesis" in window ? window.speechSynthesis.getVoices() : [];
    return (
      voices.find((v) => v.lang.startsWith("pt")) ||
      ({ name: "default", lang: "pt-BR" } as SpeechSynthesisVoice)
    );
  }

  // Obter vozes disponíveis
  getAvailableVoices(): SpeechSynthesisVoice[] {
    return "speechSynthesis" in window
      ? window.speechSynthesis.getVoices()
      : [];
  }

  // Métodos de compatibilidade (deprecated - usar getSettings)
  /** @deprecated Use getSettings('donation') instead */
  getSettingsDonation(): AlertSettings {
    return this.getSettings("donation") as AlertSettings;
  }

  /** @deprecated Use getSettings('sale') instead */
  getSettingsSale(): AlertSettings {
    return this.getSettings("sale") as AlertSettings;
  }

  /** @deprecated Use getSettings('goal') instead */
  getGoalSettings(): GoalSettings {
    return this.getSettings("goal") as GoalSettings;
  }

  /** @deprecated Use updateSettings instead */
  updateSettingsDonation(newSettings: Partial<AlertSettings>): void {
    this.updateSettings("donation", newSettings);
  }

  /** @deprecated Use updateSettings instead */
  updateSettingsSale(newSettings: Partial<AlertSettings>): void {
    this.updateSettings("sale", newSettings);
  }

  /** @deprecated Use updateSettings instead */
  updateSettingsGoal(newSettings: Partial<GoalSettings>): void {
    this.updateSettings("goal", newSettings);
  }

  /** @deprecated Retorna configurações padrão de donation */
  getDefaultSettings(): AlertSettings {
    return this.DEFAULT_SETTINGS;
  }
}
